import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import { Button } from '~/ui/buttons';
import { CloseButton } from '~/ui/buttons/CloseButton';

export type EditingMode = 'private' | 'live';

type WalkthroughTooltip = {
  title: string;
  body: string;
  position: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  arrow: string;
  anchor?: string;
};

// The top-right arrow is 20px wide and inset 20px from the callout's right edge.
const TOP_RIGHT_ARROW_CENTER_FROM_RIGHT = 30;

const rightOffsetUnder = (anchor: string): string | null => {
  const target = document.querySelector(`[data-tour="${anchor}"]`);
  if (!target) return null;

  const { left, width } = target.getBoundingClientRect();
  if (width === 0) return null;

  const fromRight =
    window.innerWidth - (left + width / 2) - TOP_RIGHT_ARROW_CENTER_FROM_RIGHT;
  return `${Math.round(fromRight)}px`;
};

const EDITING_MODE_TOOLTIPS: Record<
  EditingMode,
  { title: string; body: string }
> = {
  private: {
    title: 'You’re editing privately',
    body:
      'Current learners will finish the version they started, while new assignments will receive the updated course once published.\n\n' +
      'Existing translations will update automatically when you publish.'
  },
  live: {
    title: 'You’re editing a live course',
    body:
      'Changes apply immediately to everyone, including in-progress learners.\n\n' +
      'Added translations for a previous version? Update them again using the Translations console after you finish your changes.'
  }
};

const TooltipContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 9999;
  /* Let clicks / lower UI (e.g. EditorBanners) work outside the yellow tooltip box */
  pointer-events: none;

  b {
    font-family: ralewayBold;
    font-size: 16px;
  }

  .tooltip-container .tooltip-body-text {
    font-size: 14px;
    white-space: pre-line;
  }

  .tooltip-container {
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 427px;
    pointer-events: auto;
  }

  .closeBtn {
    position: absolute;
    top: 10px;
    left: 100%;
    margin-left: -34px;
  }

  button {
    font-family: ralewayBold;
    font-size: 14px;
  }
`;

const Tooltips = ({
  editingMode,
  productTour = false
}: {
  editingMode?: EditingMode | null;
  productTour?: boolean;
}) => {
  const {
    showTooltips,
    tooltip,
    nextTooltip,
    prevTooltip,
    setShowTooltips,
    training
  } = useEditorStore();

  useEffect(() => {
    if (window && window.document) {
      setTimeout(() => {
        const element = window.document.getElementById('tooltip-title');
        if (element) {
          element.focus();
        }
      }, 0);
    }
  }, [tooltip]);

  useEffect(() => {
    if (!showTooltips) return undefined;

    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowTooltips(false);
    };

    document.addEventListener('keydown', dismissOnEscape);
    return () => document.removeEventListener('keydown', dismissOnEscape);
  }, [showTooltips, setShowTooltips]);

  const tooltipList = useMemo<WalkthroughTooltip[]>(() => {
    const fullList = [
      {
        title: 'Publish',
        body: 'Once you are done editing, publish the course here, and assign it to your Learners.\n\n',
        position: {
          right: '50px',
          top: '75px'
        },
        arrow: 'top-right' as const,
        anchor: 'primary-action'
      },
      {
        // Step index must stay === COMPLIANCE_CONTENT_TOOLTIP_INDEX (tourConstants.ts)
        title: 'Legal Tags',
        body:
          'Legal Tags identify content that may be tied to compliance requirements.\n\n' +
          'For some courses, Legal Tags appear on specific slides. For others, Legal Tags may appear on all slides throughout the course.\n\n' +
          'Before removing or substantially modifying tagged content, consider any applicable legal or regulatory requirements.\n\n',
        position: {
          left: '255px',
          bottom: '100px'
        },
        arrow: 'bottom-left' as const
      },
      {
        title: 'Navigation',
        body: 'Browse your course outline here, organized into chapters and slides. Simply click any slide to view its content. Need to reorganize? Just drag and drop slides to create your perfect learning sequence.\n\n',
        position: {
          left: '255px',
          top: '100px'
        },
        arrow: 'left' as const
      },
      {
        title: 'Adding Slides',
        body: 'Click to add new slides or chapters. Choose from various templates with images, videos, or interactive quizzes.\n\n',
        position: {
          left: '255px',
          top: '16px'
        },
        arrow: 'left' as const
      },
      {
        title: 'Narrations',
        body: 'Bring your slide to life with voice. Upload audio files, or generate speech from text using our natural-sounding AI voices.\n\n',
        position: {
          right: '295px',
          top: '75px'
        },
        arrow: 'top-right' as const,
        anchor: 'slide-narration'
      },
      {
        title: 'Changing Images',
        body: 'Click this Edit icon to open the Media modal, where you can select and change background images, or videos.\n\n',
        position: {
          right: '5px',
          top: '125px'
        },
        arrow: 'top-right' as const
      }
    ];

    const standardTooltipList = training?.customizationOfCanonical
      ? fullList.map(item => {
          const canonicalPublishTitle =
            'Your changes are automatically saved while you edit.\n\n' +
            'Publishing makes those saved changes available as the customized version of the course.\n\n' +
            "⚠️ After publishing, you'll choose whether the original EasyLlama course or your customized version is Active for learners.\n\n";

          return item.title === 'Publish'
            ? { ...item, body: canonicalPublishTitle }
            : item;
        })
      : fullList.filter(item => item.title !== 'Legal Tags');

    if (!editingMode) return standardTooltipList;

    const editingNotice: WalkthroughTooltip = {
      ...EDITING_MODE_TOOLTIPS[editingMode],
      position: { right: '20px', top: '89px' },
      arrow: 'none'
    };

    return productTour
      ? [...standardTooltipList, editingNotice]
      : [editingNotice];
  }, [editingMode, productTour, training?.customizationOfCanonical]);

  const lastTooltipIndex = tooltipList.length - 1;
  const activeTooltipIndex =
    editingMode && !productTour
      ? lastTooltipIndex
      : Math.min(tooltip, lastTooltipIndex);
  const activeTooltip = tooltipList[activeTooltipIndex];
  const activeAnchor = activeTooltip.anchor;

  const [anchoredRight, setAnchoredRight] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!showTooltips || !activeAnchor) {
      setAnchoredRight(null);
      return undefined;
    }

    const placeUnderAnchor = () =>
      setAnchoredRight(rightOffsetUnder(activeAnchor));
    placeUnderAnchor();
    const headerChanges = new MutationObserver(placeUnderAnchor);
    headerChanges.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
    window.addEventListener('resize', placeUnderAnchor);
    return () => {
      headerChanges.disconnect();
      window.removeEventListener('resize', placeUnderAnchor);
    };
  }, [showTooltips, activeAnchor]);

  if (!showTooltips) return null;

  return (
    <TooltipContainer role="dialog" aria-labelledby="tooltip-title">
      <Tooltip
        placement={activeTooltip.arrow}
        top={activeTooltip.position.top}
        bottom={activeTooltip.position.bottom}
        left={activeTooltip.position.left}
        right={anchoredRight ?? activeTooltip.position.right}
        className="tooltip-container"
      >
        <CloseButton
          type="close"
          onClick={() => setShowTooltips(false)}
          autoFocus={false}
        />
        <b id="tooltip-title" tabIndex={0} role="heading" aria-level={2}>
          {activeTooltip.title}
        </b>
        <span className="tooltip-body-text">{activeTooltip.body}</span>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'left' }}>
          {activeTooltipIndex > 0 && (
            <Button
              onClick={prevTooltip}
              style={{
                backgroundColor: 'var(--color-yellow-lt)',
                color: 'var(--color-accent-dk)',
                border: '2px solid var(--color-accent-dk)'
              }}
            >
              Previous
            </Button>
          )}
          {activeTooltipIndex < lastTooltipIndex && (
            <Button onClick={nextTooltip} tabIndex={0}>
              Next
            </Button>
          )}
          {activeTooltipIndex === lastTooltipIndex && (
            <Button onClick={() => setShowTooltips(false)} tabIndex={0}>
              Got it
            </Button>
          )}
        </div>
      </Tooltip>
    </TooltipContainer>
  );
};

export default Tooltips;
